import React, { useMemo, useState } from "react";
import { PageHero, PageBody } from "./_shared";
import { useCidco } from "./CidcoLayout";
import { Image as ImageIcon, X as XIcon } from "lucide-react";

export default function Gallery() {
  const data = useCidco();
  const photos = data.gallery || [];
  const [lb, setLb] = useState(-1);

  const types = useMemo(() => {
    const set = new Set();
    photos.forEach((p) => p.gallery_type && set.add(p.gallery_type));
    return Array.from(set);
  }, [photos]);
  const [filter, setFilter] = useState("");
  const visible = filter ? photos.filter((p) => p.gallery_type === filter) : photos;

  return (
    <div data-testid="cid-gallery">
      <PageHero eyebrow="Snapshots" title="Inside MIT CIDCO" subtitle="A walk through our labs, classrooms, events, and student moments." accent="cyan" />
      <PageBody>
        {types.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6" data-testid="cid-gallery-filters">
            <button
              type="button"
              onClick={() => { setFilter(""); setLb(-1); }}
              data-testid="cid-gallery-filter-all"
              className={`text-sm px-4 py-2 rounded-full transition-colors ${
                filter === ""
                  ? "bg-[#0B2447] text-white font-semibold shadow-sm"
                  : "bg-white text-[#0B2447] ring-1 ring-[#E2E8F0] hover:bg-[#F4F6FA]"
              }`}
            >
              All ({photos.length})
            </button>
            {types.map((t) => {
              const count = photos.filter((p) => p.gallery_type === t).length;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => { setFilter(t); setLb(-1); }}
                  data-testid={`cid-gallery-filter-${t.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`text-sm px-4 py-2 rounded-full transition-colors ${
                    filter === t
                      ? "bg-[#06B6D4] text-white font-semibold shadow-sm"
                      : "bg-white text-[#0B2447] ring-1 ring-[#E2E8F0] hover:bg-[#F4F6FA]"
                  }`}
                >
                  {t} ({count})
                </button>
              );
            })}
          </div>
        )}

        {visible.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center ring-1 ring-[#E2E8F0]">
            <ImageIcon size={36} strokeWidth={1.2} className="mx-auto text-[#475569]" />
            <div className="mt-3 text-sm text-[#475569]">
              {filter ? `No photos in "${filter}" yet.` : "Photos will appear here once published."}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {visible.map((g, i) => (
              <button key={g.id} onClick={() => setLb(i)}
                      data-testid={`cid-gallery-photo-${g.id}`}
                      className="aspect-square overflow-hidden rounded-xl ring-1 ring-[#E2E8F0] hover:ring-2 hover:ring-[#FF6B1A] transition group">
                <img src={g.image_url} alt={g.caption || ""} loading="lazy"
                     className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
              </button>
            ))}
          </div>
        )}

        {lb >= 0 && visible[lb] && (
          <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setLb(-1)}>
            <button className="absolute top-4 right-4 text-white" onClick={() => setLb(-1)} aria-label="Close"><XIcon size={28} /></button>
            <figure onClick={(e) => e.stopPropagation()} className="max-w-5xl">
              <img src={visible[lb]?.image_url} alt={visible[lb]?.caption || ""} className="max-w-full max-h-[80vh] object-contain" />
              {visible[lb]?.caption && <figcaption className="mt-3 text-center text-white/90 text-sm">{visible[lb].caption}</figcaption>}
            </figure>
          </div>
        )}
      </PageBody>
    </div>
  );
}
